import { CommonModule } from '@angular/common';
import { Component, Input } from '@angular/core';
import { ThemePreviewKind, ThemePreviewState, ThemePreviewVisualBlock } from '../services/theme-preview.models';

@Component({
  selector: 'app-theme-preview',
  standalone: true,
  imports: [CommonModule],
  template: `
    <section
      class="preview-shell"
      [class.preview-shell--team]="kind === 'team'"
      [class.preview-shell--projector]="kind === 'projector'"
      [style]="themeStyle"
      [style.background-image]="backgroundImage"
      [style.color]="theme('--color-text')">
      @if (kind === 'team') {
        <div class="preview-hero">
          <div class="preview-visual"><img [src]="visualUrl('team1Left', 'image')" alt="" /></div>
          <div class="preview-hero-copy">
            <span class="preview-eyebrow">United Team Building</span>
            <h2>{{ state?.team1Name }}</h2>
          </div>
          <div class="preview-visual"><img [src]="visualUrl('team1Right', 'image')" alt="" /></div>
        </div>
        <section class="preview-card game-status">
          <header class="preview-section-header preview-section-header--inline">
            <h3>GAME STATUS</h3>
            <span class="preview-status-pill">Preview</span>
          </header>
          <div class="preview-round-score-row">
            <div class="preview-round-flow-stack">
              <p class="preview-round-line">ROUND {{ state?.currentRound?.roundNumber }}</p>
              <p class="preview-round-flow-line"><span class="preview-game-flow-badge">Game Flow: {{ state?.currentRound?.gameFlowModeKey || 'Mode ' + state?.currentRound?.gameFlowModeId }}</span></p>
            </div>
            <div class="preview-score-stack">
              <span>Score</span>
              <strong>{{ state?.team1Score }}</strong>
              <small>Wrong {{ state?.team1WrongAnswerCount }}/{{ state?.maxWrongAttempts }}</small>
            </div>
          </div>
        </section>
        <section class="preview-card preview-question">
          <header class="preview-section-header"><h3>QUESTION</h3></header>
          <p>{{ state?.isCurrentRoundQuestionVisible ? state?.currentRound?.question : 'Hidden until Random Team is pressed' }}</p>
        </section>
        <section class="preview-card preview-current-players">
          <header class="preview-section-header"><h3>CURRENT PLAYERS</h3></header>
          <p class="preview-current-player">Current Player: {{ currentMemberName(1) }}</p>
          <div class="preview-members preview-members--medium">
            @for (member of state?.team1Members ?? []; track member.orderNumber) {
              <span [class.preview-member--current]="member.orderNumber === state?.team1CurrentMemberOrderNumber">
                <b>{{ member.orderNumber }}.</b> {{ member.name || '—' }}
              </span>
            }
          </div>
        </section>
      } @else {
        <div class="preview-hero">
          <div class="preview-visual"><img [src]="visualUrl('projectorLeft', 'image')" alt="" /></div>
          <div class="preview-hero-copy">
            <span class="preview-eyebrow">United Team Building</span>
            <h2>{{ state?.eventName }}</h2>
            <span class="preview-label">Round {{ state?.currentRound?.roundNumber }}</span>
          </div>
          <div class="preview-visual"><img [src]="visualUrl('projectorRight', 'image')" alt="" /></div>
        </div>
        <section class="preview-card preview-team-scores">
          <header class="preview-section-header"><h3>TEAM SCORES</h3></header>
          <div class="preview-score-grid">
            <article class="preview-score-card" [class.preview-score-card--selected]="state?.selectedTeamNumber === 1">
              <div class="preview-score-card-header">
                <span class="preview-team-name-pill" [class.preview-team-name-pill--selected]="state?.selectedTeamNumber === 1">{{ state?.team1Name }}</span>
                <span class="preview-current-player-inline">{{ currentMemberName(1) }}</span>
              </div>
              <strong class="preview-score">{{ state?.team1Score }}</strong>
              <small>Wrong: {{ state?.team1WrongAnswerCount }} / {{ state?.maxWrongAttempts }}</small>
            </article>
            <article class="preview-score-card" [class.preview-score-card--selected]="state?.selectedTeamNumber === 2">
              <div class="preview-score-card-header">
                <span class="preview-team-name-pill" [class.preview-team-name-pill--selected]="state?.selectedTeamNumber === 2">{{ state?.team2Name }}</span>
                <span class="preview-current-player-inline">{{ currentMemberName(2) }}</span>
              </div>
              <strong class="preview-score">{{ state?.team2Score }}</strong>
              <small>Wrong: {{ state?.team2WrongAnswerCount }} / {{ state?.maxWrongAttempts }}</small>
            </article>
          </div>
        </section>
        <div class="preview-card preview-question">
          <header class="preview-section-header"><h3>QUESTION</h3></header>
          <p>{{ state?.isCurrentRoundQuestionVisible ? state?.currentRound?.question : 'Hidden until Random Team is pressed' }}</p>
        </div>
        <div class="preview-card preview-players-answers">
          <header class="preview-section-header"><h3>PLAYERS ANSWERS</h3></header>
          <div class="preview-projector-members">
            <div class="preview-member-card preview-member-card--projector">
              <h3>{{ state?.team1Name }} Members</h3>
              <div class="preview-member-list">
                @for (member of state?.team1Members ?? []; track member.orderNumber) {
                  <span [class.preview-member--current]="member.orderNumber === state?.team1CurrentMemberOrderNumber">
                    <b>{{ member.orderNumber }}.</b> {{ member.name || '—' }}
                  </span>
                }
              </div>
            </div>
            <div class="preview-answers">
              @for (answer of state?.currentRound?.answers ?? []; track answer.orderNumber) {
                <article class="preview-answer-tile" [class.preview-answer-tile--active]="answer.isRevealed">
                  <span>Answer {{ answer.orderNumber }}</span>
                  @if (answer.isRevealed) { <strong>{{ answer.points }}</strong> }
                  <p>{{ answer.isRevealed ? answer.text : 'Hidden answer' }}</p>
                  @if (answer.hint) { <small>Hint: {{ answer.hint }}</small> }
                </article>
              }
            </div>
            <div class="preview-member-card preview-member-card--projector">
              <h3>{{ state?.team2Name }} Members</h3>
              <div class="preview-member-list">
                @for (member of state?.team2Members ?? []; track member.orderNumber) {
                  <span [class.preview-member--current]="member.orderNumber === state?.team2CurrentMemberOrderNumber">
                    <b>{{ member.orderNumber }}.</b> {{ member.name || '—' }}
                  </span>
                }
              </div>
            </div>
          </div>
        </div>
      }

      <div class="preview-visual preview-visual--body" [class.preview-visual--body-team]="kind === 'team'" [class.preview-visual--body-projector]="kind === 'projector'">
        <img [src]="visualUrl('bodyMain', 'image')" alt="" />
      </div>
    </section>
  `,
  styles: [`
    :host { display: block; }
    .preview-shell { background-color: var(--color-bg-start, #020617); background-position: center; background-repeat: no-repeat; background-size: cover; border: 1px solid var(--color-border, #475569); border-radius: .75rem; color: var(--color-text, #f8fafc); display: grid; font-family: var(--font-family-base, sans-serif); gap: 1rem; min-height: 360px; padding: 1rem; }
    .preview-shell--team { margin: 0 auto; max-width: 720px; }
    .preview-shell--projector { margin: 0 auto; max-width: 1280px; }
    .preview-hero { align-items: center; display: grid; gap: .8rem; grid-template-columns: minmax(48px, 1fr) minmax(0, 3fr) minmax(48px, 1fr); }
    .preview-hero-copy { background: var(--color-card-background, rgba(15, 23, 42, .82)); border: 1px solid var(--color-border, #475569); border-radius: .7rem; padding: .8rem; text-align: center; }
    .preview-hero-copy h2 { font-family: var(--font-family-heading, sans-serif); font-size: var(--font-size-title, 1.8rem); margin: .2rem 0; }
    .preview-eyebrow { color: var(--color-accent-soft, #93c5fd); font-size: var(--font-size-eyebrow, .75rem); letter-spacing: .12em; text-transform: uppercase; }
    .preview-visual { align-items: center; display: flex; justify-content: center; min-height: 52px; overflow: hidden; }
    .preview-visual img { max-height: 130px; max-width: 100%; object-fit: contain; }
    .preview-card { background: var(--color-card-background, rgba(15, 23, 42, .82)); border: 1px solid var(--color-border, #475569); border-radius: 1rem; display: grid; gap: .35rem; padding: 1rem 1.25rem; }
    .preview-section-header { margin-bottom: .65rem; }
    .preview-section-header h3 { color: var(--color-text, #f8fafc); font-family: var(--font-family-heading, sans-serif); font-size: var(--font-size-section-heading, 1.25rem); letter-spacing: .14em; margin: 0; text-transform: uppercase; }
    .preview-section-header--inline { align-items: center; display: flex; gap: .75rem; justify-content: space-between; }
    .preview-status-pill { background: color-mix(in srgb, var(--color-surface-2) 90%, transparent); border: 1px solid var(--color-border); border-radius: 999px; color: var(--color-muted); font-size: var(--font-size-body); font-weight: 600; padding: .2rem .65rem; }
    .preview-round-score-row { align-items: start; display: flex; gap: .85rem; justify-content: space-between; }
    .preview-round-flow-stack { display: grid; gap: .45rem; }
    .preview-round-line { color: var(--color-text); font-size: clamp(1.5rem, 3vw, 2.25rem); font-weight: 700; margin: 0; }
    .preview-round-flow-line { margin: 0; }
    .preview-game-flow-badge { align-items: center; background: color-mix(in srgb, var(--color-accent-soft) 18%, transparent); border: 1px solid color-mix(in srgb, var(--color-accent-soft) 50%, transparent); border-radius: 999px; color: var(--color-accent-soft); display: inline-flex; font-size: .82rem; font-weight: 600; letter-spacing: .06em; padding: .2rem .7rem; text-transform: uppercase; white-space: nowrap; }
    .preview-score-stack { display: grid; gap: .2rem; justify-items: end; text-align: right; }
    .preview-score-stack span { color: var(--color-muted); font-size: var(--font-size-eyebrow); text-transform: uppercase; }
    .preview-score-stack strong { color: var(--color-text); font-size: var(--font-size-score); }
    .preview-score-stack small { color: var(--color-muted); font-size: var(--font-size-body); font-weight: 600; }
    .preview-question p { color: var(--color-muted); font-size: var(--font-size-question); margin: 0; }
    .preview-status, .preview-score-grid { display: grid; gap: .7rem; grid-template-columns: repeat(3, 1fr); }
    .preview-score-grid { grid-template-columns: repeat(2, 1fr); }
    .preview-score-card { background: color-mix(in srgb, var(--color-surface-2) 88%, transparent); border: 1px solid var(--color-border); border-radius: .85rem; display: grid; gap: .3rem; padding: .85rem; }
    .preview-score-card--selected { border-color: color-mix(in srgb, #4ade80 55%, transparent); }
    .preview-score-card-header { align-items: center; display: flex; gap: .75rem; justify-content: space-between; }
    .preview-team-name-pill { background: color-mix(in srgb, var(--color-surface-1) 75%, transparent); border: 1px solid var(--color-border); border-radius: 999px; color: var(--color-text); display: inline-flex; font-size: clamp(1.05rem, 2vw, 1.35rem); font-weight: 700; letter-spacing: .04em; margin: 0; padding: .2rem .7rem; text-transform: uppercase; }
    .preview-team-name-pill--selected { background: color-mix(in srgb, #4ade80 18%, var(--color-surface-2)); border-color: color-mix(in srgb, #4ade80 55%, transparent); }
    .preview-current-player-inline { color: var(--color-text); font-size: var(--font-size-current-player); margin: 0; text-align: right; }
    .preview-score-card small { color: var(--color-muted); font-size: var(--font-size-body); font-weight: 600; }
    .preview-label { color: var(--color-muted, #cbd5e1); font-size: var(--font-size-eyebrow, .75rem); text-transform: uppercase; }
    .preview-card p { font-size: var(--font-size-question, 1.15rem); margin: 0; }
    .preview-score { font-size: var(--font-size-score, 2.5rem); }
    .preview-members, .preview-answers { display: flex; flex-wrap: wrap; gap: .45rem; }
    .preview-members span, .preview-answers span { background: var(--color-surface-2, rgba(30, 41, 59, .82)); border: 1px solid var(--color-border, #475569); border-radius: 999px; padding: .3rem .55rem; }
    .preview-members--medium { display: grid; gap: .3rem; }
    .preview-members--medium span { align-items: center; background: transparent; border: 0; border-radius: 0; display: grid; font-size: var(--font-size-member, 2rem); gap: .45rem; grid-template-columns: 2.1rem minmax(0, 1fr); padding: 0; }
    .preview-current-player { background: var(--color-card-background); border: 1px solid var(--color-border); border-radius: 999px; color: var(--color-accent-soft); font-size: var(--font-size-current-player); font-weight: 600; margin: 0 0 .65rem; padding: .35rem .75rem; }
    .preview-projector-members { display: grid; gap: .7rem; grid-template-columns: minmax(150px, 1fr) minmax(0, 2fr) minmax(150px, 1fr); }
    .preview-member-card { background: var(--color-card-background, rgba(15, 23, 42, .82)); border: 1px solid var(--color-border, #475569); border-radius: .7rem; padding: .8rem; }
    .preview-member-card h3 { font-size: 1rem; margin: 0 0 .55rem; }
    .preview-member-card--projector h3 { font-size: 1.15rem; }
    .preview-member-list { display: grid; gap: .35rem; }
    .preview-member-list span { color: var(--color-muted, #cbd5e1); font-size: 1rem; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
    .preview-players-answers { min-width: 0; }
    .preview-answers { display: grid; gap: .65rem; grid-template-columns: repeat(2, minmax(0, 1fr)); }
    .preview-answer-tile { background: color-mix(in srgb, var(--color-surface-2) 90%, transparent); border: 1px solid var(--color-border); border-radius: .85rem; display: grid; gap: .25rem; min-height: 5.75rem; padding: .75rem; }
    .preview-answer-tile--active { border-color: color-mix(in srgb, var(--color-accent) 52%, var(--color-border)); box-shadow: 0 0 0 1px color-mix(in srgb, var(--color-accent) 20%, transparent); }
    .preview-answer-tile span { color: var(--color-muted); font-size: var(--font-size-eyebrow); text-transform: uppercase; }
    .preview-answer-tile p { font-size: var(--font-size-answer); margin: 0; }
    .preview-answer-tile strong { color: var(--color-accent-soft); font-size: var(--font-size-score); }
    .preview-answer-tile small { color: var(--color-accent-soft); font-size: var(--font-size-body); }
    .preview-member--current { border-color: var(--color-accent, #3b82f6) !important; color: var(--color-accent-soft, #93c5fd); }
    .preview-card small { color: var(--color-muted, #cbd5e1); }
    .preview-visual--body { align-self: center; aspect-ratio: 1 / 1; background: transparent; border: 0; border-radius: 0; min-height: 0; overflow: hidden; width: min(100%, 540px); }
    .preview-visual--body-team { justify-self: center; }
    .preview-visual--body img { background: transparent; height: 100%; max-height: none; max-width: none; object-fit: contain; width: 100%; }
    .preview-visual--body-projector { width: min(42vw, 540px); }
    @media (max-width: 840px) { .preview-projector-members { grid-template-columns: 1fr; } }
    @media (max-width: 640px) { .preview-status, .preview-score-grid, .preview-answers { grid-template-columns: 1fr; } .preview-hero { grid-template-columns: 1fr; } }
  `]
})
export class ThemePreviewComponent {
  @Input({ required: true }) state: ThemePreviewState | null = null;
  @Input({ required: true }) kind: ThemePreviewKind = 'team';
  @Input() themeVariables: Record<string, string> = {};

  get backgroundImage(): string {
    const background = this.kind === 'team' ? this.state?.visuals?.teamBackground : this.state?.visuals?.projectorBackground;
    return background?.localUrl ? `url("${background.localUrl}")` : `linear-gradient(180deg, ${this.theme('--color-bg-start')} 0%, ${this.theme('--color-bg-end')} 100%)`;
  }

  get themeStyle(): string {
    return Object.entries(this.themeVariables)
      .map(([name, value]) => `${name}: ${value}`)
      .join('; ');
  }

  theme(name: string): string {
    return this.themeVariables[name] ?? '';
  }

  visualUrl(blockKey: string, layer: keyof ThemePreviewVisualBlock): string {
    const reference = this.state?.visuals?.blocks[blockKey]?.[layer];
    return reference?.localUrl ?? '';
  }

  currentMemberName(teamNumber: 1 | 2): string {
    const members = teamNumber === 1 ? this.state?.team1Members ?? [] : this.state?.team2Members ?? [];
    const orderNumber = teamNumber === 1
      ? this.state?.team1CurrentMemberOrderNumber
      : this.state?.team2CurrentMemberOrderNumber;
    return members.find(member => member.orderNumber === orderNumber)?.name || '—';
  }
}
